import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import Placeholder from "components/Layout/Placeholder";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { RecordPromptProps } from "components/Prompt/RecordPrompt";
import { mergeNewRecordDefaults } from "components/Form/newRecordDefaults";
import { seedParentReferenceInitialValues } from "components/Form/relationshipInitialValues";
import {
    isRelationshipFormLoading,
    needsFullResourceFetch,
} from "components/Form/relationshipFormLoading";
import { isRelationshipConfirmDisabled } from "components/Form/relationshipConfirmDisabled";
import { computeRelationshipSubmitPayload } from "components/Form/relationshipSubmitPayload";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { getRecordConfig, getWriteOnlyFieldsWithData } from "config/resourceUtils";
import objectTemplateAttributeConfig from "config/resources/object_template_attribute";
import {
    classifyAttributeUpdate,
    isAttributeImpactCancelled,
} from "config/resources/objectTemplateAttributeImpact";
import { confirmAttributeImpact } from "config/resources/objectTemplateAttributeImpactDialog";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { resourceDataSelector } from "containers/Resources/Resources.selectors";
import { ResourceEditFields } from "containers/Resources/components/ResourceEditFields/ResourceEditFields";
import { valueAndSchema } from "containers/Resources/screens/ResourcesSingle/constants";
import {
    fetchSingleResourceData,
    filterObjectTemplateDataTypeList,
    getResourceDataInitialValues,
    newRecordId as newId,
    setValueDependingOnDataType,
    storeFormData,
    submitRelationshipRecordForm,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { useEffect, useMemo, useState } from "react";
import { Form as FinalForm, FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import strings from "utils/strings";
import { getIri } from "utils/utils";
import "./RelationshipRecordForm.scss";

const ObjectTemplateAttributeSync = ({
    values,
    enabled,
}: {
    values: Record<string, unknown> | undefined;
    enabled: boolean;
}) => {
    useEffect(() => {
        if (!enabled || values == null) {
            return;
        }
        setValueDependingOnDataType(values);
        filterObjectTemplateDataTypeList((values.id as string | undefined) ?? "new");
    }, [enabled, values]);
    return null;
};

// The role_permission "Object template" refinement is only meaningful for
// CUSTOM_OBJECT grants (see role_permission.tsx — the picker is shown only for
// that record type). If the user changes the record type away from CUSTOM_OBJECT
// after picking a template, clear the now-hidden value so a stale (and
// backend-rejected) object_template_id is never submitted.
const RolePermissionScopeSync = ({
    values,
    form,
    enabled,
}: {
    values: Record<string, unknown> | undefined;
    form: { change: (name: string, value: unknown) => void };
    enabled: boolean;
}) => {
    const tableType = values?.table_type;
    const objectTemplateId = values?.object_template_id;
    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (objectTemplateId && tableType !== "CUSTOM_OBJECT") {
            form.change("object_template_id", null);
        }
    }, [enabled, tableType, objectTemplateId, form]);
    return null;
};

interface RelationshipRecordFormProps extends RecordPromptProps {
    isNested?: boolean;
    setNestedFormSaveData?: (value: any) => void;
    nestedFormSaveData?: { [key: string]: () => void };
    keepDirtyOnReinitialize?: boolean;
    defaultValues?: any;
    newRecordId?: string;
}

export const RelationshipRecordForm = ({
    data,
    onConfirm,
    onCancel,
    isNested,
    isOpen,
    setNestedFormSaveData,
    keepDirtyOnReinitialize = true,
    defaultValues,
    newRecordId = newId,
}: RelationshipRecordFormProps) => {
    const { resourceName, recordId, parentResourceType, parentResourceId, isReadOnly, isDisabled } =
        data;
    const resourceConfig = getRecordConfig(resourceName);

    const history = useHistory();
    const dispatch = useDispatch();
    const iri = getIri(resourceName, recordId);
    const resourceData = useSelector(resourceDataSelector(iri));

    const writeOnlyFieldsWithData = getWriteOnlyFieldsWithData(resourceName, resourceData);

    // The record id whose FULL single-resource fetch has completed. Derived
    // synchronously into `hasLoadedFull` (by comparing against the record currently
    // being edited) so switching records flips the loading gate within the same
    // render — no post-paint effect delay that could flash stale data.
    const [loadedRecordId, setLoadedRecordId] = useState<string | null>(null);
    const hasLoadedFull = !!recordId && loadedRecordId === recordId;

    const fetchResourceData = () => {
        if (!recordId) {
            return Promise.resolve();
        }

        return fetchSingleResourceData({ dispatch, resourceName, iri, recordId });
    };

    // Fetch the full single resource once per open for an existing record and keep
    // the form hidden until it resolves. We deliberately do NOT skip the fetch when
    // a cached `resourceData` already exists: that entry may be partial (the list /
    // live-subscription populate the same slice with only a subset of columns), and
    // rendering the form from it dropped the unshown fields (description, is_hidden,
    // silent_update, is_nullable), which the save then diffed away to empty/false.
    useEffect(() => {
        if (!needsFullResourceFetch({ isOpen, recordId, hasLoadedFull })) {
            return;
        }
        let cancelled = false;
        fetchResourceData().then(() => {
            if (!cancelled) {
                setLoadedRecordId(recordId ?? null);
            }
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, recordId, hasLoadedFull]);

    const isLoading = isRelationshipFormLoading({ recordId, hasLoadedFull });

    const isNew = useMemo(() => !recordId, [recordId]);
    const initialValues = useMemo(
        () =>
            // For a NEW relationship record, seed the parent foreign-key into the
            // form's initial values so field components can read the parent id
            // *during editing* — not only at submit (see
            // seedParentReferenceInitialValues). (Stefan, 2026-10-12.)
            seedParentReferenceInitialValues(
                getResourceDataInitialValues(resourceConfig, resourceData),
                { isNew, parentResourceType, parentResourceId }
            ),
        [resourceConfig, resourceData, isNew, parentResourceType, parentResourceId]
    );

    const [isSaveEnabledManually, setSaveEnabledManually] = useState(false);
    // Set only when a create request has actually succeeded. final-form's own
    // submitSucceeded also turns true when onSubmit REJECTS, so it cannot gate
    // Confirm (see relationshipConfirmDisabled.ts).
    const [createSucceeded, setCreateSucceeded] = useState(false);

    const submitRecordForm = (formData) =>
        submitRelationshipRecordForm({
            dispatch,
            formData,
            recordId,
            resourceName,
            additionalData: { parentResourceType, parentResourceId },
            resourceConfig,
            setNestedFormSaveData,
            newRecordId,
            additionalParams: { commit: false },
        });

    const isObjectTemplateAttribute = useMemo(
        () => objectTemplateAttributeConfig.resourceName === resourceName,
        [resourceName]
    );
    const CustomFormTag: keyof JSX.IntrinsicElements = `${isNested ? "div" : "form"}`;

    const { defaultSchemaValues } = useMatchingFields({ activeTab: resourceName });

    const defaultFormValues = useMemo(
        () =>
            mergeNewRecordDefaults(
                defaultSchemaValues,
                resourceConfig?.formDTO?.newRecordDefaults,
                defaultValues
            ),
        [defaultSchemaValues, resourceConfig, defaultValues]
    );

    return isLoading ? (
        <div className="spacer">
            <Placeholder />
        </div>
    ) : (
        <div className="RelationshipRecordForm">
            <FinalForm
                onSubmit={async (formData, form) => {
                    if (isObjectTemplateAttribute) {
                        // Tell the user how many custom objects the change
                        // affects before saving (rejects on cancel).
                        const initial = form.getState().initialValues as any;
                        const change = isNew
                            ? { kind: "add" as const, attributeName: formData?.name }
                            : classifyAttributeUpdate(initial, formData);
                        await confirmAttributeImpact({
                            objectTemplateId:
                                initial?.object_template_id ??
                                (parentResourceType === "object_template"
                                    ? parentResourceId
                                    : undefined),
                            changes: change ? [change] : [],
                        });
                    }
                    const pending = submitRecordForm(
                        computeRelationshipSubmitPayload({
                            isNew,
                            initialValues: form.getState().initialValues,
                            formData,
                            defaultFormValues,
                            valueAndSchemaKey: valueAndSchema,
                        })
                    );
                    // No promise = input could not be processed; nothing was sent.
                    if (!pending) {
                        return undefined;
                    }
                    const result = await pending;
                    if (isNew) {
                        setCreateSucceeded(true);
                    }
                    return result;
                }}
                initialValues={initialValues}
                defaultValues={defaultFormValues}
                // special case for role_permission:
                // when initialValues are still fetching, the defaults are set
                // with keepDirtyOnReinitialize=false we can overwrite defaults when initialValues are already fetched
                keepDirtyOnReinitialize={keepDirtyOnReinitialize}
            >
                {({
                    handleSubmit,
                    pristine,
                    submitting,
                    hasValidationErrors,
                    form,
                    values,
                    initialValues,
                }) => {
                    return (
                        <CustomFormTag
                            id="recordPromptForm"
                            className="recordPromptForm"
                            onSubmit={() => {
                                if (!pristine) handleSubmit();
                            }}
                        >
                            {!isNested && (
                                <div className="recordPrompt__header">
                                    <div className="recordPrompt__header-title">
                                        <span>{resourceConfig.label}</span>
                                        {resourceConfig.labelDocs ? (
                                            <DocsInfoTooltip
                                                title={resourceConfig.labelDocs.title}
                                                urlPath={resourceConfig.labelDocs.urlPath}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            )}
                            <FormSpy subscription={{ values: true }}>
                                {({ values: formValues }) => (
                                    <ObjectTemplateAttributeSync
                                        values={formValues}
                                        enabled={isObjectTemplateAttribute}
                                    />
                                )}
                            </FormSpy>
                            <RolePermissionScopeSync
                                values={values}
                                form={form}
                                enabled={resourceConfig.resourceName === "role_permission"}
                            />
                            <BlockingNavigationModal
                                navigate={history.push}
                                shouldBlockNavigation={!pristine}
                                titleText={strings.navigateAwayUnsavedPrompt}
                                submitForm={hasValidationErrors ? undefined : handleSubmit}
                                priority={NAVIGATION_PRIORITIES.NESTED_FORM}
                            />
                            <SubmitLock locked={submitting}>
                                <ResourceEditFields
                                    formStructure={resourceConfig.form}
                                    isDisabled={isDisabled}
                                    isReadOnly={isReadOnly}
                                    writeOnlyFieldsWithData={writeOnlyFieldsWithData}
                                    recordId={recordId}
                                    resourceTypeName={resourceName}
                                    currentValues={values}
                                    defaultValues={defaultFormValues}
                                    enableSaveManually={setSaveEnabledManually}
                                    additionalFieldProps={{
                                        hasInitialValuesLoaded:
                                            !recordId || initialValues !== undefined,
                                    }}
                                />
                            </SubmitLock>
                            <div className="button-container">
                                {onCancel && (
                                    <CancelButton onClick={onCancel} disabled={submitting} />
                                )}
                                <Button
                                    className="confirm-button"
                                    variation="primary"
                                    loading={submitting}
                                    disabled={isRelationshipConfirmDisabled({
                                        hasValidationErrors,
                                        isNew,
                                        pristine,
                                        submitting,
                                        createSucceeded,
                                        isSaveEnabledManually,
                                    })}
                                    onClick={() => {
                                        handleSubmit()
                                            ?.then(() => fetchResourceData())
                                            ?.then(() => {
                                                onConfirm?.();
                                            })
                                            ?.catch((error) => {
                                                // Cancelling the impact notice keeps the form open.
                                                if (!isAttributeImpactCancelled(error)) {
                                                    throw error;
                                                }
                                            });
                                    }}
                                    label="Confirm"
                                />
                            </div>
                            <FormSpy
                                onChange={({ pristine }) => {
                                    if (setNestedFormSaveData) {
                                        storeFormData({
                                            pristine,
                                            setNestedFormSaveData,
                                            recordId: recordId,
                                            form,
                                            isNew,
                                            newRecordId,
                                            fetchResourceData,
                                            onConfirm,
                                        });
                                    }
                                }}
                            />
                        </CustomFormTag>
                    );
                }}
            </FinalForm>
        </div>
    );
};
